/**
 * Module System — Controls which features are available per tenant.
 * 
 * Modules:
 * - pharmacy: POS, Inventory, Prescriptions, Orders, Invoices
 * - clinic: Reception, Examination, Lab, Prescription Templates
 * - full: All features
 */

export type AppModule = 'pharmacy' | 'clinic' | 'full';

export interface ModuleConfig {
  label: string;
  description: string;
  routes: string[];
  sidebarSections: {
    clinical: boolean;
    features: boolean | string[]; // true = all, string[] = specific items
  };
}

export const MODULE_CONFIG: Record<AppModule, ModuleConfig> = {
  pharmacy: {
    label: 'Nhà thuốc',
    description: 'Quản lý bán hàng, tồn kho, đơn thuốc',
    routes: ['/pos', '/inventory', '/prescriptions', '/orders', '/invoices'],
    sidebarSections: {
      clinical: false,
      features: true,
    },
  },
  clinic: {
    label: 'Phòng khám',
    description: 'Tiếp nhận, khám bệnh, xét nghiệm',
    routes: ['/reception', '/examination', '/lab', '/prescription-templates', '/prescriptions'],
    sidebarSections: {
      clinical: true,
      features: ['prescriptions'], // Only show Đơn thuốc from features
    },
  },
  full: {
    label: 'Phòng khám + Nhà thuốc',
    description: 'Tất cả tính năng',
    routes: ['*'],
    sidebarSections: {
      clinical: true,
      features: true,
    },
  },
};

// Routes accessible regardless of module
export const SHARED_ROUTES = ['/settings', '/users', '/compensation', '/services', '/login', '/unauthorized'];

/**
 * Check if a route is allowed for the given module.
 */
export function isRouteAllowed(module: AppModule, pathname: string): boolean {
  const config = MODULE_CONFIG[module];
  if (!config) return true; // fallback: allow all

  // Shared routes always allowed
  if (SHARED_ROUTES.some(r => pathname.startsWith(r))) return true;

  // API routes always allowed (backend handles its own auth)
  if (pathname.startsWith('/api')) return true;

  // Full module allows everything
  if (config.routes.includes('*')) return true;

  // Check if pathname starts with any allowed route
  return config.routes.some(r => pathname.startsWith(r));
}

/**
 * Check if a sidebar item should be shown for the given module.
 */
export function isSidebarItemAllowed(module: AppModule, href: string, section: 'clinical' | 'features'): boolean {
  const config = MODULE_CONFIG[module];
  if (!config) return true;

  const sectionConfig = config.sidebarSections[section];

  if (typeof sectionConfig === 'boolean') return sectionConfig;

  // Array of specific route prefixes to show
  if (Array.isArray(sectionConfig)) {
    return sectionConfig.some(r => href.startsWith(`/${r}`));
  }

  return true;
}
